import { useNavigate } from "react-router-dom";
import { useFgp } from "@/context/FgpContext";
import { PageHeader } from "@/components/PageHeader";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Users, Heart, Briefcase, UserPlus } from "lucide-react";

export default function RelationshipView() {
  const navigate = useNavigate();
  const { people } = useFgp();

  const active = people.filter((p) => !p.archived);
  const principalPerson = active.find((p) => p.role === "Principal") || active[0];
  const others = principalPerson ? active.filter((p) => p.id !== principalPerson.id) : [];
  const family = others.filter((p) => p.role !== "Staff");
  const staff = others.filter((p) => p.role === "Staff");

  const Node = ({ person, label, delay }) => (
    <motion.button
      initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay }}
      onClick={() => navigate(`/app/people/${person.id}`)}
      data-testid={`rel-node-${person.id}`}
      className="group flex flex-col items-center gap-2 w-32"
    >
      <Avatar className="h-16 w-16 ring-2 ring-white shadow-md group-hover:ring-neutral-300 transition-all">
        <AvatarImage src={person.avatar} alt={person.name} />
        <AvatarFallback>{person.name[0]}</AvatarFallback>
      </Avatar>
      <div className="text-center">
        <p className="text-sm font-semibold text-slate-800 leading-tight">{person.name.split(" ")[0]}</p>
        <p className="text-xs text-slate-400">{label}</p>
      </div>
    </motion.button>
  );

  return (
    <div>
      <PageHeader title="Relationship View" subtitle="How everyone in the network connects to the principal — updates live as you add, archive or remove people." />

      {!principalPerson ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 flex flex-col items-center text-center" data-testid="rel-empty">
          <div className="h-14 w-14 rounded-2xl bg-neutral-50 text-neutral-500 flex items-center justify-center mb-4">
            <Users className="h-7 w-7" />
          </div>
          <p className="font-heading text-lg font-semibold text-slate-900">No people yet</p>
          <p className="text-sm text-slate-400 mt-1 max-w-sm">Add your first person to start building your network. The relationship map updates automatically.</p>
          <Button onClick={() => navigate("/app/people")} className="mt-6 gap-2" data-testid="rel-add-person">
            <UserPlus className="h-4 w-4" /> Add a person
          </Button>
        </div>
      ) : (
      <>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 lg:p-12 overflow-x-auto">
        <div className="flex flex-col items-center min-w-[640px]">
          <motion.button initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            onClick={() => navigate(`/app/people/${principalPerson.id}`)}
            className="flex flex-col items-center gap-2 mb-2" data-testid="rel-principal">
            <div className="relative">
              <Avatar className="h-24 w-24 ring-4 ring-neutral-100 shadow-lg">
                <AvatarImage src={principalPerson.avatar} alt={principalPerson.name} />
                <AvatarFallback>{principalPerson.name[0]}</AvatarFallback>
              </Avatar>
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[10px] font-semibold uppercase tracking-wider bg-neutral-600 text-white px-2 py-0.5 rounded-full">Principal</span>
            </div>
            <p className="mt-2 font-heading font-semibold text-slate-900">{principalPerson.name}</p>
          </motion.button>

          <div className="h-10 w-px bg-slate-200" />
          <div className="h-px w-3/4 bg-slate-200" />

          <div className="w-full mt-8">
            <div className="flex items-center gap-2 justify-center mb-6">
              <Heart className="h-4 w-4 text-rose-400" />
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Family &amp; close network</p>
            </div>
            {family.length ? (
              <div className="flex flex-wrap justify-center gap-6">
                {family.map((p, i) => <Node key={p.id} person={p} label={p.relation || p.role} delay={i * 0.05} />)}
              </div>
            ) : <p className="text-center text-sm text-slate-400">No family members yet.</p>}
          </div>

          <div className="h-px w-3/4 bg-slate-200 my-10" />

          <div className="w-full">
            <div className="flex items-center gap-2 justify-center mb-6">
              <Briefcase className="h-4 w-4 text-neutral-400" />
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Household staff</p>
            </div>
            {staff.length ? (
              <div className="flex flex-wrap justify-center gap-6">
                {staff.map((p, i) => <Node key={p.id} person={p} label={p.relation || p.role} delay={i * 0.05} />)}
              </div>
            ) : <p className="text-center text-sm text-slate-400">No staff yet.</p>}
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        {[
          { icon: Users, label: "Active people", value: active.length },
          { icon: Heart, label: "Family & network", value: family.length + 1 },
          { icon: Briefcase, label: "Household staff", value: staff.length },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-4">
            <div className="h-10 w-10 rounded-lg bg-neutral-50 text-neutral-600 flex items-center justify-center"><s.icon className="h-5 w-5" /></div>
            <div><p className="font-heading text-2xl font-semibold text-slate-900">{s.value}</p><p className="text-xs text-slate-400">{s.label}</p></div>
          </div>
        ))}
      </div>
      </>
      )}
    </div>
  );
}
